import React, { useEffect, useMemo, useState } from 'react';

import { message, Modal, Tabs } from 'antd';

import type { RoleResponse } from '@/shared/api/users';
import { MODAL_FOOTER_BUTTON_SIZE, MODAL_WIDTHS } from '@/shared/lib/constants';
import { ROUTE_LABELS, ROUTES } from '@/shared/lib/constants/routes';
import {
  getRolePermissions,
  type RolePermissionsMap,
  saveRolePermissions,
} from '@/shared/lib/permissions/rolePermissions';
import { getAllCheckableKeys, type TreeNode } from '@/shared/lib/tree';
import { Button } from '@/shared/ui/Button';
import { Tree } from '@/shared/ui/Tree';

import { useIcebergTreeData } from '@/features/user-management/hooks/useIcebergTreeData';
import { TablePermissionTree } from '@/features/user-management/ui/TablePermissionTree/TablePermissionTree';

import styles from './RolePermissionModal.module.css';

type RolePermissionModalProps = {
  open: boolean;
  role: RoleResponse | null;
  onClose: () => void;
};

export const RolePermissionModal: React.FC<RolePermissionModalProps> = ({ open, role, onClose }) => {
  const [activeTab, setActiveTab] = useState<string>('screen');
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
  const [tableCheckedKeys, setTableCheckedKeys] = useState<React.Key[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([
    'data',
    'workflow',
    'experiment',
    'governance',
    'admin',
  ]);

  const { treeData: icebergTreeData, isLoading: isIcebergTreeLoading } = useIcebergTreeData();

  const routeTreeData = useMemo<TreeNode[]>(() => {
    const isAdminRole = role?.name === 'admin';

    const routes: TreeNode[] = [
      {
        key: 'data',
        title: '데이터',
        count: 3,
        isLeaf: false,
        children: [
          { key: ROUTES.HOME, title: ROUTE_LABELS[ROUTES.HOME], isLeaf: true },
          { key: ROUTES.UNIFIED_SEARCH, title: ROUTE_LABELS[ROUTES.UNIFIED_SEARCH], isLeaf: true },
          { key: ROUTES.LINEAGE, title: ROUTE_LABELS[ROUTES.LINEAGE], isLeaf: true },
        ],
      },
      {
        key: 'workflow',
        title: '워크플로우',
        count: 2,
        isLeaf: false,
        children: [
          { key: ROUTES.DAG, title: ROUTE_LABELS[ROUTES.DAG], isLeaf: true },
          { key: ROUTES.CONNECTORS, title: ROUTE_LABELS[ROUTES.CONNECTORS], isLeaf: true },
        ],
      },
      {
        key: 'experiment',
        title: '실험',
        count: 1,
        isLeaf: false,
        children: [{ key: ROUTES.EXPERIMENTS, title: ROUTE_LABELS[ROUTES.EXPERIMENTS], isLeaf: true }],
      },
      {
        key: 'governance',
        title: '거버넌스',
        count: 3,
        isLeaf: false,
        children: [
          { key: ROUTES.DOMAIN, title: ROUTE_LABELS[ROUTES.DOMAIN], isLeaf: true },
          { key: ROUTES.GLOSSARY, title: ROUTE_LABELS[ROUTES.GLOSSARY], isLeaf: true },
          { key: ROUTES.TAGS, title: ROUTE_LABELS[ROUTES.TAGS], isLeaf: true },
        ],
      },
      {
        key: 'admin',
        title: '관리',
        count: 3,
        isLeaf: false,
        disabled: isAdminRole,
        children: [
          {
            key: ROUTES.ADMIN_SETTINGS,
            title: ROUTE_LABELS[ROUTES.ADMIN_SETTINGS],
            isLeaf: true,
            disabled: isAdminRole,
          },
          {
            key: ROUTES.ADMIN_MONITORING,
            title: ROUTE_LABELS[ROUTES.ADMIN_MONITORING],
            isLeaf: true,
            disabled: isAdminRole,
          },
          {
            key: ROUTES.ADMIN_PERFORMANCE,
            title: ROUTE_LABELS[ROUTES.ADMIN_PERFORMANCE],
            isLeaf: true,
            disabled: isAdminRole,
          },
        ],
      },
    ];

    return routes;
  }, [role]);

  useEffect(() => {
    if (!role || !open) return;

    const customPermissions = getRolePermissions();
    const checkedRoutes: React.Key[] = [];

    const hasCustomPermissions = customPermissions && Array.isArray(customPermissions[role.name]);

    if (hasCustomPermissions) {
      const allowedRoutes = customPermissions[role.name];
      routeTreeData.forEach((node) => {
        if (node.children) {
          node.children.forEach((child) => {
            if (allowedRoutes.includes(child.key as string)) {
              checkedRoutes.push(child.key);
            }
          });
        }
      });
    } else {
      if (role.name === 'admin') {
        routeTreeData.forEach((node) => {
          checkedRoutes.push(node.key);
          if (node.children) {
            node.children.forEach((child) => checkedRoutes.push(child.key));
          }
        });
      } else if (role.name === 'manager') {
        routeTreeData.forEach((node) => {
          if (node.key !== 'admin') {
            checkedRoutes.push(node.key);
            if (node.children) {
              node.children.forEach((child) => checkedRoutes.push(child.key));
            }
          }
        });
      } else {
        routeTreeData.forEach((node) => {
          if (node.key === 'data' || node.key === 'workflow' || node.key === 'experiment') {
            checkedRoutes.push(node.key);
            if (node.children) {
              node.children.forEach((child) => checkedRoutes.push(child.key));
            }
          }
        });
      }
    }

    setCheckedKeys(checkedRoutes);
  }, [role, routeTreeData, open]);

  useEffect(() => {
    if (open && !isIcebergTreeLoading && icebergTreeData.length > 0) {
      const allKeys = getAllCheckableKeys(icebergTreeData);
      setTableCheckedKeys(allKeys);
    }
  }, [open, icebergTreeData, isIcebergTreeLoading]);

  const handleSave = () => {
    if (!role) return;

    let allowedRoutes = (checkedKeys as string[]).filter(
      (key) => !['data', 'workflow', 'experiment', 'governance', 'admin'].includes(key)
    );

    if (role.name === 'admin') {
      const adminRoutes = [ROUTES.ADMIN_SETTINGS, ROUTES.ADMIN_MONITORING, ROUTES.ADMIN_PERFORMANCE];
      allowedRoutes = [...new Set([...allowedRoutes, ...adminRoutes])];
    }

    const existingPermissions = getRolePermissions() || {};
    const updatedPermissions: RolePermissionsMap = {
      ...existingPermissions,
      [role.name]: allowedRoutes,
    };

    saveRolePermissions(updatedPermissions);

    message.success('권한이 저장되었습니다');
  };

  return (
    <Modal
      title={`권한 관리: ${role?.name || ''}`}
      open={open}
      onCancel={onClose}
      width={MODAL_WIDTHS.STANDARD_FORM}
      destroyOnHidden={true}
      footer={
        <div className={styles.footer}>
          <Button variant="outlined" color="standard" onClick={onClose} {...MODAL_FOOTER_BUTTON_SIZE}>
            닫기
          </Button>
          <Button variant="filled" color="standard" onClick={handleSave} disabled {...MODAL_FOOTER_BUTTON_SIZE}>
            저장
          </Button>
        </div>
      }>
      <div className={styles.modalContent}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'screen',
              label: '화면별 접근권한',
              children: (
                <div className={styles.tabContent}>
                  <div className={styles.treeContainer}>
                    <Tree
                      key={`tree-${role?.name}-${checkedKeys.length}`}
                      checkable
                      showCount
                      disabled
                      treeData={routeTreeData}
                      checkedKeys={checkedKeys}
                      expandedKeys={expandedKeys}
                      onCheck={(checked) => {
                        setCheckedKeys(checked as React.Key[]);
                      }}
                      onExpand={(keys) => {
                        setExpandedKeys(keys);
                      }}
                    />
                  </div>
                </div>
              ),
            },
            {
              key: 'table',
              label: '테이블별 접근권한',
              children: (
                <div className={styles.tabContent}>
                  <div className={styles.treeContainer}>
                    <TablePermissionTree checkedKeys={tableCheckedKeys} onCheck={setTableCheckedKeys} />
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>
    </Modal>
  );
};
